import { exceedsDragThreshold } from '../state/planGesture';
import { isMacPlatform } from './hotkeys';

/** How long a finger rests before the press opens the context menu, as iPadOS. */
export const LONG_PRESS_MS = 500;

export interface ContextPress { x: number; y: number; pointerType: string }
/** What a surface made of a press: the floor point under it, and whether the selection now is the press's own choice. */
export interface ContextPressResult { point: { x: number; z: number } | null; decided: boolean }

/**
 * A right button (a Mac's Ctrl+click) released within the drag threshold, or a finger held `LONG_PRESS_MS`, on a canvas inside `root`. Not
 * `contextmenu`: a Mac fires it on press, before a drag shows, and an iPad never does. `armed` is asked at both ends.
 */
/** How long after the finger lifts its click may still come; past it a click is the user's own (Enter on a row). */
const LIFT_CLICK_MS = 400;

export function watchContextPress(root: HTMLElement, armed: () => boolean, open: (press: ContextPress) => void): () => void {
  let press: { id: number; x: number; y: number; touch: boolean; button: number } | null = null, timer = 0;
  let swallow: { id: number; timer: number } | null = null;
  const drop = () => { press = null; window.clearTimeout(timer); timer = 0; };
  const release = () => { if (swallow) window.clearTimeout(swallow.timer); swallow = null; };
  const onCanvas = (target: EventTarget | null) => target instanceof Element && target.closest('canvas') !== null && root.contains(target);
  // A Mac's Ctrl+click is its right-click: a one-button mouse or a trackpad without a secondary click.
  const secondary = (event: PointerEvent) => event.button === 2 || (event.button === 0 && event.ctrlKey && event.pointerType === 'mouse' && isMacPlatform());
  const down = (event: PointerEvent) => {
    release();
    if (press?.touch && event.pointerType === 'touch') { drop(); return; }
    drop();
    if (!onCanvas(event.target) || !armed()) return;
    if (event.pointerType === 'touch') {
      press = { id: event.pointerId, x: event.clientX, y: event.clientY, touch: true, button: event.button };
      timer = window.setTimeout(() => {
        const held = press; drop();
        if (!held || !armed()) return;
        // The finger lifts over the menu: the click it makes then must not press a row.
        swallow = { id: held.id, timer: 0 };
        open({ x: held.x, y: held.y, pointerType: 'touch' });
      }, LONG_PRESS_MS);
    } else if (secondary(event)) press = { id: event.pointerId, x: event.clientX, y: event.clientY, touch: false, button: event.button };
  };
  const move = (event: PointerEvent) => {
    if (press?.id === event.pointerId && exceedsDragThreshold(event.clientX - press.x, event.clientY - press.y)) drop();
  };
  const lifted = (event: PointerEvent) => {
    const held = swallow;
    if (held?.id === event.pointerId && !held.timer) held.timer = window.setTimeout(() => { if (swallow === held) swallow = null; }, LIFT_CLICK_MS);
  };
  const up = (event: PointerEvent) => {
    lifted(event);
    const held = press;
    if (held?.id !== event.pointerId) return;
    drop();
    if (!held.touch && event.button === held.button && !exceedsDragThreshold(event.clientX - held.x, event.clientY - held.y) && armed())
      open({ x: event.clientX, y: event.clientY, pointerType: event.pointerType });
  };
  const cancel = (event: PointerEvent) => { lifted(event); if (press?.id === event.pointerId) drop(); };
  const click = (event: MouseEvent) => {
    if (!swallow) return;
    release(); event.preventDefault(); event.stopPropagation();
  };
  window.addEventListener('pointerdown', down, true); window.addEventListener('pointermove', move, true);
  window.addEventListener('pointerup', up, true); window.addEventListener('pointercancel', cancel, true); window.addEventListener('click', click, true);
  return () => {
    drop(); release();
    window.removeEventListener('pointerdown', down, true); window.removeEventListener('pointermove', move, true);
    window.removeEventListener('pointerup', up, true); window.removeEventListener('pointercancel', cancel, true); window.removeEventListener('click', click, true);
  };
}
